import { useQuery } from '@tanstack/react-query';
import { getReviewId } from './Review';
import { NonRetriableError } from 'query/errors';
import {
  REVIEW_STAGE_CONTRIBUTION,
  REVIEW_STAGE_STATISTICS,
  REVIEW_SUMMARY,
  tokenizeRoute,
} from 'query/routes';

export type ReviewStage = 'screen' | 'select' | 'included';

export type CategoryCounts = {
  screen: number;
  select: number;
  included: number;
  excluded: number;
  irrelevant: number;
};

export type ReviewSummary = {
  hasExtractionTemplates: boolean;
  categoryCounts: CategoryCounts;
};

export type StageCounters = {
  complete: number;
  partiallyComplete: number;
  consensusRequired: number;
  notStarted: number;
  available: number;
};

type ReviewSummaryResponse = {
  has_extraction_templates: boolean;
  category_counts: CategoryCounts;
};

type StageCountersResponse = {
  complete: number;
  partially_complete: number;
  consensus_required: number;
  not_started: number;
  available: number;
};

type StageContributionResponse = {
  contribution: number;
};

const requireReviewId = () => {
  const reviewId = getReviewId();
  if (!reviewId)
    throw new Error("Couldn't get Review ID. Is this part of a review?");
  return reviewId;
};

// A failed statistics request is usually a timeout; retrying automatically
// would repeat the same expensive query, so leave retrying to the user.
const getJson = async <T>(
  route: string,
  tokens: Record<string, string> = {}
): Promise<T> => {
  const url = tokenizeRoute(route, { review_id: requireReviewId(), ...tokens });
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new NonRetriableError(
      String(response.status),
      `Request to ${url.pathname} failed`
    );
  }

  return response.json();
};

export const getReviewSummary = async (): Promise<ReviewSummary> => {
  const response = await getJson<ReviewSummaryResponse>(REVIEW_SUMMARY);

  return {
    hasExtractionTemplates: response.has_extraction_templates,
    categoryCounts: response.category_counts,
  };
};

export const getStageCounters = async (
  stage: ReviewStage
): Promise<StageCounters> => {
  const response = await getJson<StageCountersResponse>(
    REVIEW_STAGE_STATISTICS,
    { stage }
  );

  return {
    complete: response.complete,
    partiallyComplete: response.partially_complete,
    consensusRequired: response.consensus_required,
    notStarted: response.not_started,
    available: response.available,
  };
};

export const getStageContribution = async (
  stage: ReviewStage
): Promise<number> => {
  const response = await getJson<StageContributionResponse>(
    REVIEW_STAGE_CONTRIBUTION,
    { stage }
  );

  return response.contribution;
};

const STATISTICS_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

export const useReviewSummary = () =>
  useQuery({
    queryKey: ['reviewSummary', getReviewId()],
    queryFn: getReviewSummary,
    ...STATISTICS_QUERY_OPTIONS,
  });

export const useStageCounters = (stage: ReviewStage) =>
  useQuery({
    queryKey: ['reviewStageCounters', getReviewId(), stage],
    queryFn: () => getStageCounters(stage),
    ...STATISTICS_QUERY_OPTIONS,
  });

export const useStageContribution = (stage: ReviewStage) =>
  useQuery({
    queryKey: ['reviewStageContribution', getReviewId(), stage],
    queryFn: () => getStageContribution(stage),
    ...STATISTICS_QUERY_OPTIONS,
  });
